import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { X, ArrowLeft, Trash2, Plus } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

// Candidate fields a {{n}} param can map to (resolved server-side at send time).
// Static text is also allowed — anything that isn't a known token is sent verbatim.
const CANDIDATE_TOKENS = [
    { label: "First Name", token: "{{candidate_first_name}}" },
    { label: "Full Name", token: "{{candidate_name}}" },
    { label: "Role", token: "{{candidate_role}}" },
    { label: "Company", token: "{{candidate_company}}" },
    { label: "Location", token: "{{candidate_location}}" },
];

const emptyForm = { name: "", meta_template_name: "", language_code: "en", body: "", category: "", params: [] };

// Highest placeholder number referenced by the body ({{1}}, {{2}}, …). Meta params are
// positional, so we size the param list to 1..max and require no gaps.
const maxPlaceholder = (body) => {
    const nums = [...(body || "").matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1]));
    return nums.length ? Math.max(...nums) : 0;
};

const missingPlaceholders = (body, count) => {
    const present = new Set([...(body || "").matchAll(/\{\{(\d+)\}\}/g)].map(m => Number(m[1])));
    const gaps = [];
    for (let i = 1; i <= count; i++) if (!present.has(i)) gaps.push(i);
    return gaps;
};

const renderPreview = (body, params) =>
    (body || "").replace(/\{\{(\d+)\}\}/g, (m, n) => params[Number(n) - 1] || m);

/**
 * Developer-only utility to manage approved Meta WhatsApp templates.
 * Opens to a list of saved templates with an "Add Template" form.
 * Recruiters never see this — they only pick registered templates in the sequence builder.
 */
const WhatsAppTemplateDevModal = ({ isOpen, onClose }) => {
    const [view, setView] = useState("list");        // "list" | "create"
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchTemplates = () => {
        setLoading(true);
        axios.get(`${SOURCING_URL}/whatsapp/templates`)
            .then(({ data }) => setTemplates(data))
            .catch(() => setError("Failed to load WhatsApp templates."))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (isOpen) { setView("list"); setForm(emptyForm); setError(null); fetchTemplates(); }
    }, [isOpen]);

    const paramCount = useMemo(() => maxPlaceholder(form.body), [form.body]);

    const setBody = (body) => {
        const count = maxPlaceholder(body);
        setForm(f => ({ ...f, body, params: Array.from({ length: count }, (_, i) => f.params[i] ?? "") }));
    };
    const setParam = (idx, value) => setForm(f => ({ ...f, params: f.params.map((p, i) => (i === idx ? value : p)) }));
    const appendToken = (idx, token) => setForm(f => ({ ...f, params: f.params.map((p, i) => (i === idx ? `${p}${token}` : p)) }));

    const openCreate = () => { setForm(emptyForm); setError(null); setView("create"); };

    const handleSave = async () => {
        if (!form.name.trim() || !form.meta_template_name.trim() || !form.body.trim()) {
            setError("Name, Meta template name and body are required."); return;
        }
        const gaps = missingPlaceholders(form.body, paramCount);
        if (gaps.length) {
            setError(`Placeholders must be numbered 1..${paramCount} with no gaps (missing {{${gaps.join("}}, {{")}}}).`); return;
        }
        if (form.params.some(p => !p.trim())) {
            setError("Every {{n}} param needs a mapping (a candidate field or static text)."); return;
        }
        setSaving(true);
        setError(null);
        try {
            const { data } = await axios.post(`${SOURCING_URL}/whatsapp/templates`, {
                name: form.name,
                meta_template_name: form.meta_template_name,
                language_code: form.language_code || "en",
                body: form.body,
                params: form.params,
                category: form.category || null,
            });
            setTemplates(prev => [data, ...prev]);
            setForm(emptyForm);
            setView("list");
        } catch (e) {
            setError(e.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleArchive = async (id) => {
        if (!window.confirm("Archive this WhatsApp template? It won't be selectable in new steps.")) return;
        await axios.delete(`${SOURCING_URL}/whatsapp/templates/${id}`);
        setTemplates(prev => prev.filter(t => t.id !== id));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
            <div className="w-[640px] max-h-[90vh] bg-white dark:bg-[#1B1524] rounded-2xl flex flex-col overflow-hidden shadow-2xl">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700/50 shrink-0">
                    <div className="flex items-center gap-2">
                        {view === "create" && (
                            <button onClick={() => setView("list")} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200">
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                        )}
                        <div>
                            <h2 className="text-base font-semibold text-gray-800 dark:text-white">
                                {view === "create" ? "Register WhatsApp Template" : "WhatsApp Templates"}
                            </h2>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500">Developer utility — seed approved Meta templates into the DB.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-5 h-5" /></button>
                </div>

                {view === "list" ? (
                    <>
                        <div className="px-6 py-4 overflow-y-auto flex flex-col gap-3">
                            {loading ? (
                                <div className="flex justify-center py-12">
                                    <div className="w-7 h-7 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : templates.length === 0 ? (
                                <p className="text-sm text-gray-400 dark:text-gray-500 py-8 text-center">
                                    No WhatsApp templates yet. Add an approved Meta template to use it in outreach.
                                </p>
                            ) : (
                                templates.map(t => (
                                    <div key={t.id} className="bg-white dark:bg-white/5 rounded-xl border border-gray-200 dark:border-gray-600 px-4 py-3 flex items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-medium text-gray-800 dark:text-white">{t.name}</p>
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 font-mono">{t.meta_template_name}</span>
                                                <span className="text-[11px] text-gray-400 dark:text-gray-500">{t.language_code}</span>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{renderPreview(t.body, t.params || [])}</p>
                                        </div>
                                        <button onClick={() => handleArchive(t.id)} className="text-gray-300 dark:text-gray-600 hover:text-red-400 shrink-0" title="Archive">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                            {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 shrink-0">
                            <button onClick={openCreate}
                                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium text-white"
                                style={{ background: "linear-gradient(to right, #25D366, #128C7E)" }}>
                                <Plus className="w-4 h-4" /> Add Template
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="px-6 py-5 flex flex-col gap-4 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Display Name</label>
                                    <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                        className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-400" placeholder="e.g. Shortlist Notice" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Meta Template Name</label>
                                    <input value={form.meta_template_name} onChange={e => setForm(f => ({ ...f, meta_template_name: e.target.value }))}
                                        className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-400" placeholder="e.g. shortlist_notice" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Language code</label>
                                    <input value={form.language_code} onChange={e => setForm(f => ({ ...f, language_code: e.target.value }))}
                                        className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-400" placeholder="en" />
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Category (optional)</label>
                                    <input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                                        className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-green-400" placeholder="e.g. outreach" />
                                </div>
                            </div>

                            <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Body (numbered placeholders, exactly as approved in Meta)</label>
                                <textarea rows={4} value={form.body} onChange={e => setBody(e.target.value)}
                                    className="w-full border border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 text-sm outline-none focus:border-green-400 resize-none"
                                    placeholder={"Hello {{1}}\n\nYour profile has been shortlisted."} />
                            </div>

                            {paramCount > 0 && (
                                <div className="flex flex-col gap-2">
                                    <label className="text-xs text-gray-500 dark:text-gray-400">Map each variable (candidate field token or static text)</label>
                                    {form.params.map((p, i) => (
                                        <div key={i} className="flex items-center gap-2">
                                            <span className="text-xs font-mono text-green-600 dark:text-green-400 w-9 shrink-0">{`{{${i + 1}}}`}</span>
                                            <input value={p} onChange={e => setParam(i, e.target.value)}
                                                className="flex-1 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-green-400"
                                                placeholder="{{candidate_name}} or static text" />
                                            <div className="flex items-center gap-1 flex-wrap shrink-0 max-w-[42%] justify-end">
                                                {CANDIDATE_TOKENS.map(t => (
                                                    <button key={t.token} onClick={() => appendToken(i, t.token)} title={t.token}
                                                        className="text-[11px] px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-100">
                                                        {t.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {form.body && (
                                <div>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Preview</label>
                                    <div className="bg-[#e5ddd5] rounded-xl px-3 py-3">
                                        <div className="bg-white dark:bg-white/5 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-white max-w-[80%] shadow-sm whitespace-pre-wrap">
                                            {renderPreview(form.body, form.params)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {error && <p className="text-xs text-red-500 dark:text-red-400">{error}</p>}
                        </div>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-gray-700/50 shrink-0">
                            <button onClick={() => setView("list")} className="px-5 py-2 rounded-full text-sm border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500">Cancel</button>
                            <button onClick={handleSave} disabled={saving}
                                className="px-5 py-2 rounded-full text-sm font-medium text-white disabled:opacity-50"
                                style={{ background: "linear-gradient(to right, #25D366, #128C7E)" }}>
                                {saving ? "Saving..." : "Save Template"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default WhatsAppTemplateDevModal;
